import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - token ekle
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - 401 hatalarını yakala
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword })
};

// Users
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`)
};

// Companies
export const companiesAPI = {
  getAll: (params) => api.get('/companies', { params }),
  get: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  getContracts: (id) => api.get(`/companies/${id}/contracts`),
  getScores: (id) => api.get(`/companies/${id}/scores`)
};

// Work Orders
export const workOrdersAPI = {
  getAll: (params) => api.get('/work-orders', { params }),
  get: (id) => api.get(`/work-orders/${id}`),
  create: (data) => api.post('/work-orders', data),
  update: (id, data) => api.put(`/work-orders/${id}`, data),
  delete: (id) => api.delete(`/work-orders/${id}`),
  getDashboard: () => api.get('/work-orders/dashboard'),
  getAssignmentStatus: () => api.get('/work-orders/assignment-status')
};

// Assignments
export const assignmentsAPI = {
  getByWorkOrder: (workOrderId) => api.get(`/work-orders/${workOrderId}/assignments`),
  create: (formData) => api.post('/work-orders/assignments', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/work-orders/assignments/${id}`),
  downloadContract: (id) => `${API_URL}/work-orders/assignments/${id}/contract`,
  viewContract: (id) => `${API_URL}/work-orders/assignments/${id}/contract/view`
};

// Processes
export const processesAPI = {
  getByWorkOrder: (workOrderId) => api.get(`/work-orders/${workOrderId}/processes`),
  create: (workOrderId, data) => api.post(`/work-orders/${workOrderId}/processes`, data),
  update: (id, data) => api.put(`/work-orders/processes/${id}`, data),
  delete: (id) => api.delete(`/work-orders/processes/${id}`),
  complete: (id) => api.post(`/work-orders/processes/${id}/complete`),
  reorder: (workOrderId, orders) => api.post(`/work-orders/${workOrderId}/processes/reorder`, { orders })
};

// QC
export const qcAPI = {
  getAll: (params) => api.get('/qc', { params }),
  create: (data) => api.post('/qc', data),
  review: (id, formData) => api.put(`/qc/${id}/review`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadReport: (id) => `${API_URL}/qc/${id}/report`
};

// Scoring
export const scoringAPI = {
  getRules: () => api.get('/scoring/rules'),
  createRule: (data) => api.post('/scoring/rules', data),
  updateRule: (id, data) => api.put(`/scoring/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/scoring/rules/${id}`),
  addScore: (data) => api.post('/scoring/scores', data),
  getCompanyScores: (companyId) => api.get(`/scoring/company/${companyId}`)
};

// Extensions
export const extensionsAPI = {
  getAll: (params) => api.get('/extensions', { params }),
  create: (data) => api.post('/extensions', data),
  review: (id, status) => api.put(`/extensions/${id}/review`, { status })
};

// Notifications
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  create: (data) => api.post('/notifications', data),
  update: (id, data) => api.put(`/notifications/${id}`, data),
  delete: (id) => api.delete(`/notifications/${id}`)
};

// Kolay erişim için helper fonksiyonlar
const apiHelpers = {
  // Auth
  login: (email, password) => authAPI.login(email, password),
  me: () => authAPI.me(),
  changePassword: (currentPassword, newPassword) => authAPI.changePassword(currentPassword, newPassword),

  // Users
  getUsers: (params) => usersAPI.getAll(params),
  getUser: (id) => usersAPI.get(id),
  createUser: (data) => usersAPI.create(data),
  updateUser: (id, data) => usersAPI.update(id, data),
  deleteUser: (id) => usersAPI.delete(id),

  // Companies
  getCompanies: (params) => companiesAPI.getAll(params),
  getCompany: (id) => companiesAPI.get(id),
  createCompany: (data) => companiesAPI.create(data),
  updateCompany: (id, data) => companiesAPI.update(id, data),
  deleteCompany: (id) => companiesAPI.delete(id),
  getCompanyContracts: (id) => companiesAPI.getContracts(id),
  getCompanyScores: (id) => companiesAPI.getScores(id),

  // Work Orders
  getWorkOrders: (params) => workOrdersAPI.getAll(params),
  getWorkOrder: (id) => workOrdersAPI.get(id),
  createWorkOrder: (data) => workOrdersAPI.create(data),
  updateWorkOrder: (id, data) => workOrdersAPI.update(id, data),
  deleteWorkOrder: (id) => workOrdersAPI.delete(id),
  getDashboard: () => workOrdersAPI.getDashboard(),
  getAssignmentStatus: () => workOrdersAPI.getAssignmentStatus(),

  // Assignments
  getAssignments: (workOrderId) => assignmentsAPI.getByWorkOrder(workOrderId),
  createAssignment: (formData) => assignmentsAPI.create(formData),
  deleteAssignment: (id) => assignmentsAPI.delete(id),

  // Processes
  getProcesses: (workOrderId) => processesAPI.getByWorkOrder(workOrderId),
  createProcess: (workOrderId, data) => processesAPI.create(workOrderId, data),
  updateProcess: (id, data) => processesAPI.update(id, data),
  deleteProcess: (id) => processesAPI.delete(id),
  completeProcess: (id) => processesAPI.complete(id),
  reorderProcesses: (workOrderId, orders) => processesAPI.reorder(workOrderId, orders),

  // QC
  getQcRequests: (params) => qcAPI.getAll(params),
  createQcRequest: (data) => qcAPI.create(data),
  reviewQc: (id, formData) => qcAPI.review(id, formData),

  // Scoring
  getScoringRules: () => scoringAPI.getRules(),
  createScoringRule: (data) => scoringAPI.createRule(data),
  updateScoringRule: (id, data) => scoringAPI.updateRule(id, data),
  deleteScoringRule: (id) => scoringAPI.deleteRule(id),
  addScore: (data) => scoringAPI.addScore(data),

  // Extensions
  getExtensions: (params) => extensionsAPI.getAll(params),
  createExtension: (data) => extensionsAPI.create(data),
  approveExtension: (id) => extensionsAPI.review(id, 'approved'),
  rejectExtension: (id, reason) => api.put(`/extensions/${id}/review`, { status: 'rejected', rejectionReason: reason }),

  // Notifications
  getNotificationSettings: (params) => notificationsAPI.getAll(params),
  createNotificationSetting: (data) => notificationsAPI.create(data),
  updateNotificationSetting: (id, data) => notificationsAPI.update(id, data),
  deleteNotificationSetting: (id) => notificationsAPI.delete(id)
};

export default apiHelpers;
